import React, { useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { DateTime, Duration } from "luxon"
import { FiInfo } from "react-icons/fi"
import { IoIosArrowUp, IoIosArrowDown } from "react-icons/io";

import "./index.scss"
import { ConfigContainer } from "components"
import { Title, Text, Container, DurationInput, Select, TaskStagePicker, LocationPicker, ColorPicker, Checkbox } from "components/Form"
import { message } from "state_management"
import isOnMobile from "utils/scripts/isOnMobile"
import { Button } from 'react-bootstrap'
import { FiArrowLeft } from 'react-icons/fi'
import ProjectConfigurationPage from "components/Config/ProjectConfigurationPage"
import useT from "utils/hooks/useT"

// We want to display a warning to the user
// if they provide bad input
function validateAction(obj, t) {
  if (obj.earliestStart && obj.deadline) {
    const earliestStart = DateTime.fromISO(obj.earliestStart)
    const deadline = DateTime.fromISO(obj.deadline)
    if (deadline < earliestStart) {
      return t("deadlineMustBeLaterThanEarliestStart")
    }
    if (earliestStart.plus({ second: obj.duration }) > deadline) {
      return t("notEnoughRoomForDuration")
    }
  }
  return null
}

// Config used for actions, actions in project templates and unplanned actions.
export default function ActionConfig({
  action,
  title,
  templateAction = false,
  isGraphNode = false,
  isSingleAction = false,
  isStandardAction: isStandardActionProp = false,
  submitHandler,
  deleteTitle,
  deleteHandler,
  shiftDeleteHandler,
  confirmDeletionText,
  cancelHandler,
  projectId,
  showCurrentMonthOnClick,
  bookedAndFreeAssets,
  disabled = false,
}) {
  const dispatch = useDispatch()
  const { t } = useT("common")

  // Different action types. These and some of the props decides what should be
  // dispalyed to the user. The Logic is quite "hacky", and could be made better
  const unplannedAction = !action.start
  const scheduleAction = !templateAction && !isGraphNode && !unplannedAction
  const singleActionEdit = useSelector((state) =>
    state.projects[action.projectId] ? state.projects[action.projectId].singleActionProject : null
  )
  const projectName = useSelector((state) =>
    state.projects[action.projectId] ? state.projects[action.projectId].name : null
  )
  const teamTimeZone = useSelector((state) => state.teamInfo.timezone)
  const isMember = useSelector((state) => state.auth.isMember)
  const allowTaskTransfer = useSelector((state) => state.teamStatus.allowTaskTransfer)

  // Common attributes
  const competenceGroups = useSelector((state) => state.competenceGroups)
  const assets = useSelector((state) => state.asset.assets)
  const [name, setName] = useState(action.name || "")
  const [stage, setStage] = useState(action.stage || "TODO")
  const [location, setLocation] = useState(action.onSite || false)
  const [description, setDescription] = useState(action.description || "")
  const [startDate, setStartDate] = useState(action.start ? DateTime.fromISO(action.start).setZone(teamTimeZone) : null)
  const [startTime, setStartTime] = useState(
    startDate ? Duration.fromObject({ hours: startDate.hour, minutes: startDate.minute }) : null
  )
  const [allowedAssetIds, setAllowedAssetIds] = useState(action.allowedAssetIds ? action.allowedAssetIds : [])
  const [assetIds, setAssetIds] = useState(action.assetIds ? action.assetIds : [])
  const [allowedGroupIds, setAllowedGroupIds] = useState(
    action.allowedGroupIds ? action.allowedGroupIds.filter((groupId) => competenceGroups[groupId]) : []
  )
  const [duration, setDuration] = useState(Duration.fromObject({ seconds: action.duration || 0 }))
  const [earliestStart, setEarliestStart] = useState(
    action.earliestStart ? DateTime.fromISO(action.earliestStart).setZone(teamTimeZone).toFormat("yyyy-MM-dd") : null
  )
  const [earliestStartTime, setEarliestStartTime] = useState(() => {
    return action.earliestStart
      ? Duration.fromObject({
          hours: DateTime.fromISO(action.earliestStart).setZone(teamTimeZone).hour,
          minutes: DateTime.fromISO(action.earliestStart).setZone(teamTimeZone).minute,
        })
      : Duration.fromMillis(0)
  })
  const [deadline, setDeadline] = useState(action.deadline ? DateTime.fromISO(action.deadline).setZone(teamTimeZone).toFormat("yyyy-MM-dd") : null)
  const [deadlineTime, setDeadlineTime] = useState(
    action.deadline
      ? Duration.fromObject({
          hours: DateTime.fromISO(action.deadline).setZone(teamTimeZone).hour,
          minutes: DateTime.fromISO(action.deadline).setZone(teamTimeZone).minute,
        })
      : Duration.fromObject({ hours: 23, minutes: 59 })
  )
  const [teamMember, setTeamMember] = useState(action.memberIds ? [action.memberIds[0]] : [])
  const members = useSelector((state) => state.members)
  const isStandardAction = action.isStandardAction || isStandardActionProp
  const [isOperational, setIsOperational] = useState(action.id ? action.isOperational : true)
  const [color, setColor] = useState(action.color)
  const allStandardActions = useSelector((state) => state.standardActions.standardActions)
  const [editProject, setEditProject] = useState(false);

  // Displays the remaining fields in the config
  const [showRest, setShowRest] = useState(false)

  let sortedCollection = [];
  if (bookedAndFreeAssets?.freeAssets && bookedAndFreeAssets?.bookedAssets) {
    // Combine freeAssets and bookedAssets first, and mark them appropriately
    const combinedAssets = [
      ...bookedAndFreeAssets.freeAssets
        .filter(assetId => assets[assetId])
        .map(assetId => ({
          ...assets[assetId],
          status: t("assetStatusFree"),
        })),
      ...bookedAndFreeAssets.bookedAssets
        .filter(assetId => assets[assetId])
        .map(assetId => ({
          ...assets[assetId],
          status: t("assetStatusBooked"),
        }))
    ];

    // Sort the combined assets alphabetically by their name
    const sortedAssets = combinedAssets.sort((a, b) => a.name.localeCompare(b.name))
      .map(asset => ({
        ...asset,
        name: `${asset.name} (${asset.status})` // Append status (Free/Booked) to the name
      }));

    // Assign the sorted assets to sortedCollection
    sortedCollection.push(...sortedAssets);
  }

  const assetsArray = Object.values(assets).filter(asset => asset && asset.name);
  const sortedAssets = assetsArray.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      {editProject && projectId && (
        <>
          <Button className="go-back-button" variant="secondary" onClick={() => {
            setEditProject(false)
          }}>
            <FiArrowLeft />
          </Button>
          <ProjectConfigurationPage
            id={projectId}
            isCreateOrEditProject={true}
            setEditProject={() => {
              setEditProject()
            }}
          />
        </>
      )}
      {!editProject && (
          <ConfigContainer
            title={title}
            submitHandler={() => {
              let obj = {
                name,
                description,
                duration: !duration ? Duration.fromMillis(0) : duration.as("seconds"),
                allowedGroupIds,
                allowedAssetIds,
                onSite: location,
                earliestStart: !earliestStart
                  ? null
                  : earliestStartTime
                    ? DateTime.fromFormat(earliestStart, "yyyy-MM-dd").setZone(teamTimeZone)
                    .set({ hour: earliestStartTime.hours, minute: earliestStartTime.minutes })
                    .toISO()
                    : DateTime.fromFormat(earliestStart, "yyyy-MM-dd").toISO(),
                deadline:
                deadline && deadlineTime
                  ? DateTime.fromFormat(deadline, "yyyy-MM-dd").setZone(teamTimeZone)
                  .set({ hour: deadlineTime.hours, minute: deadlineTime.minutes })
                  .toISO()
                  : null,
              }
              const mes = validateAction(obj, t)
              if (mes) {
                dispatch(message.warning(mes))
                return
              }
              if (!templateAction && !unplannedAction) {
                obj.assetIds = assetIds
              }
              if (!templateAction && !isGraphNode) {
                obj.stage = stage
              }

              if (!templateAction && !unplannedAction && !isGraphNode && stage !== "TODO" && stage !== "BACKLOG") {
                obj.start = startDate && startDate.set({ hour: startTime.hours, minute: startTime.minutes }).toISO()
                obj.memberIds = teamMember
              }

              if (isStandardAction) {
                const standardAction = allStandardActions.find((project) => project.id === action.projectId);
                obj.color = standardAction ? color : (color || "#ffffff");
                obj.isOperational = isOperational;
              }

              submitHandler(obj)
            }}
            disabled={disabled}
            canCancelWhenDisabled={disabled}
            cancelHandler={cancelHandler}
            deleteTitle={deleteTitle}
            deleteHandler={deleteHandler}
            shiftDeleteHandler={shiftDeleteHandler}
            confirmDeletionText={confirmDeletionText}
            projectId={projectId}
            editProjectHandler={!isGraphNode && !isSingleAction && !isOnMobile && !singleActionEdit && !isStandardAction && !disabled && (isMember ? allowTaskTransfer : true) ? () => setEditProject(true) : null}
            canDeleteWhenDisabled={!disabled && isStandardAction}
          >
            {(scheduleAction || unplannedAction) && !isGraphNode && (!isSingleAction || scheduleAction) && (
              <div className="setup-container">
                <Container>
                  <Title>
                    {t("actionName")}<div className="red-text">*</div>
                  </Title>
                  <Text required disabled={disabled} value={name} onChange={setName} />
                  {!isSingleAction && !isStandardAction && <Text disabled value={projectName} />}
                </Container>
                {unplannedAction && !isGraphNode && !isSingleAction && (
                  <Container>
                    <Title>
                      {t("allowedCompetenceGroups")} <div className="red-text">*</div>
                    </Title>
                    <Select
                      name={t("allowedPerformers")}
                      disabled={disabled}
                      required={!templateAction}
                      collection={Object.values(competenceGroups).sort((a, b) => {
                        if (a.name.toUpperCase() < b.name.toUpperCase()) return -1
                        if (a.name.toUpperCase() > b.name.toUpperCase()) return 1
                        return 0
                      })}
                      value={allowedGroupIds}
                      onChange={setAllowedGroupIds}
                      menuPosition="fixed"
                    />
                  </Container>
                )}
                <div className={`${!(isSingleAction || unplannedAction || isGraphNode) ? "actionconfig-container-compartment" : null }`}>
                  {scheduleAction && (
                    <Container>
                      <Title>
                        {t("fromDate")} <div className="red-text">*</div>
                      </Title>
                      <Text
                        type="date"
                        required
                        disabled={disabled}
                        value={startDate ? startDate.toFormat("yyyy-MM-dd") : ""}
                        onChange={(value) => {
                          if (!value) {
                            setStartDate(undefined)
                            return
                          }
                          try {
                            const newDate = DateTime.fromFormat(value, "yyyy-MM-dd").setZone(teamTimeZone, { keepLocalTime: true })
                            setStartDate(newDate)
                          } catch (error) {
                            // Handle invalid date format
                          }
                        }}
                      />
                      <Title>
                        {t("fromTime")} <div className="red-text">*</div>
                      </Title>
                      <DurationInput disabled={disabled} value={startTime} onChange={setStartTime} required isTime/>
                    </Container>
                  )}
                  <Container>
                    <Title>
                      {t("durationHHMM")} <div className="red-text">*</div>
                    </Title>
                    <DurationInput required={!templateAction} disabled={disabled} value={duration} onChange={setDuration} />
                  </Container>
                  {scheduleAction && (
                    <Container>
                      <Title>{t("toDate")}</Title>
                      <Text
                        type="date"
                        required
                        disabled
                        value={startDate && duration ? startDate.plus(duration).toFormat("yyyy-MM-dd") : ""}
                      />
                      <Title>{t("toTime")}</Title>
                      <DurationInput disabled value={startTime && duration ? startTime.plus(duration) : null} isTime />
                    </Container>
                  )}
                </div>
                {scheduleAction && (
                  <Container>
                    <Title>
                      {t("teamMember")} <div className="red-text">*</div>
                    </Title>
                    <Select
                      required
                      closeMenuOnSelect={true}
                      attr={"initials"}
                      disabled={disabled}
                      collection={Object.values(members).filter(
                        (member) => (!member.archived || action.memberIds[0] === member.id) && member.showOnPlan
                      )}
                      value={teamMember}
                      onChange={setTeamMember}
                      isMulti={false}
                      menuPosition="fixed"
                    />
                  </Container>
                )}
                {scheduleAction && (
                  <Container>
                    <Title>{t("usedInstruments")}</Title>
                    <Select
                      disabled={disabled}
                      collection={Object.values(sortedCollection)}
                      value={assetIds}
                      onChange={setAssetIds}
                      menuPosition={"fixed"}
                    />
                  </Container>
                )}
              </div>
            )}

            {(isGraphNode || (isSingleAction && !scheduleAction)) && (
              <>
                <div className="setup-container">
                  <Container>
                    <Title>
                      {t("actionName")} <div className="red-text">*</div>
                    </Title>
                    <Text required disabled={disabled} value={name} onChange={setName} />
                    {!templateAction && !isSingleAction && <Text disabled value={projectName} />}
                  </Container>
                  {(isSingleAction || isGraphNode) && (
                    <Container>
                      <Title>
                        {t("allowedCompetenceGroups")}<div className="red-text">*</div>
                      </Title>
                      <Select
                        name={t("allowedPerformers")}
                        disabled={disabled}
                        required={!templateAction}
                        collection={Object.values(competenceGroups).sort((a, b) => {
                          if (a.name.toUpperCase() < b.name.toUpperCase()) return -1
                          if (a.name.toUpperCase() > b.name.toUpperCase()) return 1
                          return 0
                        })}
                        value={allowedGroupIds}
                        onChange={setAllowedGroupIds}
                        menuPosition="fixed"
                      />
                    </Container>
                  )}
                  <div className={`${!(isSingleAction || unplannedAction || isGraphNode) ? "actionconfig-container-compartment" : null }`}>
                    <Container>
                      <Title>
                        {t("durationHHMM")} <div className="red-text">*</div>
                      </Title>
                      <DurationInput required={!templateAction} disabled={disabled} value={duration} onChange={setDuration} />
                    </Container>
                  </div>
                  {isSingleAction && !scheduleAction && action.id && (
                    <Container>
                      <Title>{t("stage")}</Title>
                      <TaskStagePicker required disabled={disabled} value={stage} onChange={setStage} />
                    </Container>
                  )}
                </div>
              </>
            )}
            <div className="action-links" onClick={() => setShowRest(!showRest)}>
              <div className="action-container">
                <span>{t("additionalInformation")}</span>
                {showRest ? <IoIosArrowUp/> : <IoIosArrowDown/>}
              </div>
            </div>
            {showRest && (
              <div className="setup-container">
                {((scheduleAction || unplannedAction) && !isStandardAction) && (
                  <Container>
                    <Title>{t("stage")}</Title>
                    <TaskStagePicker required disabled={disabled} value={stage} onChange={setStage} menuPlacement="auto" />
                  </Container>
                )}
                <Container>
                  <Title>{t("description")}</Title>
                  <Text large disabled={disabled} value={description} onChange={setDescription} />
                </Container>
                {isStandardAction && (
                  <Container>
                    <Title>{t("operationalTask")}</Title>
                    <Checkbox
                      id={`action-operational-${action.id || "new"}`}
                      checked={Boolean(isOperational)}
                      disabled={disabled}
                      onChange={setIsOperational}
                    />
                  </Container>
                )}
                {!isStandardAction && (<div className="actionconfig-container-compartment">
                  <p>{t("timeConstraints")}</p>
                  <Container>
                    <Title>
                      {t("earliestStart")}&nbsp;
                    </Title>
                    <Title>{t("date")}</Title>
                    <Text
                      type="date"
                      disabled={disabled}
                      value={earliestStart || ""}
                      onChange={(value) => {
                        setEarliestStart(value)
                      }}
                    />
                    <Title>{t("timeHHMM")}</Title>
                    <DurationInput
                      disabled={disabled}
                      value={earliestStartTime}
                      onChange={setEarliestStartTime}
                      isTime
                    />
                    <span className="project-config-info">
                      {!isSingleAction && <FiInfo title={t("onlyAdvancedUsersInfo")} />}
                    </span>
                  </Container>
                  <Container>
                    <Title>{t("deadline")}</Title>
                    <Title>{t("date")}</Title>
                    <Text
                      type="date"
                      required={false}
                      disabled={disabled}
                      value={deadline || ""}
                      onChange={(value) => {
                        setDeadline(value)
                      }}
                    />
                    <Title>{t("timeHHMM")}</Title>
                    <DurationInput required={false} disabled={disabled} value={deadlineTime} onChange={setDeadlineTime} isTime />
                  </Container>
                </div>)}
                <div className="actionconfig-container-compartment">
                  <p>{isGraphNode || isSingleAction || unplannedAction ? t("instrumentConstraints") : t("competenceAndInstrumentConstraints")}</p>
                  {!isGraphNode && !unplannedAction && (
                    <Container>
                      <Title>{t("allowedCompetenceGroups")}</Title>
                      <Select
                        name={t("allowedPerformers")}
                        disabled={disabled}
                        required={!templateAction}
                        collection={Object.values(competenceGroups).sort((a, b) => {
                          if (a.name.toUpperCase() < b.name.toUpperCase()) return -1
                          if (a.name.toUpperCase() > b.name.toUpperCase()) return 1
                          return 0
                        })}
                        value={allowedGroupIds}
                        onChange={setAllowedGroupIds}
                        menuPosition="fixed"
                      />
                    </Container>
                  )}
                  <Container>
                    <Title>{t("allowedInstruments")}</Title>
                    {/* TODO: Update usedAssetIds when this changes! */}
                    <Select
                      disabled={disabled}
                      collection={sortedAssets}
                      value={allowedAssetIds}
                      onChange={setAllowedAssetIds}
                      menuPosition="fixed"
                    />
                  </Container>
                </div>
                <div className="actionconfig-container-compartment">
                  <p>{isGraphNode || isSingleAction || unplannedAction ? "" : t("location")}</p>
                  <Container>
                    <Title>{t("location")}</Title>
                    <LocationPicker
                      value={location} onChange={setLocation} menuPlacement="top" disabled={disabled}
                    />
                  </Container>
                </div>
                {isStandardAction && action.stage !== "PLANNED" &&(
                  <Container>
                    <Title>
                      {t("color")}
                    </Title>
                    <div style={{maxWidth: "93.8%"}}>
                      <ColorPicker required={false} value={color} onChange={setColor}/>
                    </div>
                  </Container>
                )}
              </div>
            )}
          </ConfigContainer>
    )}
    </>
  )
}
